'use client';

import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { FieldWrapper } from '../../components/field-wrapper';
import { ApiDocumentationRequiredAlert } from '../documents-reports/application-documentation/ApiDocumentationRequiredAlert';
import { ApplicationDocumentationCompactFileInput } from '../documents-reports/application-documentation/ApplicationDocumentationCompactFileInput';
import { ApplicationDocumentationFileList } from '../documents-reports/application-documentation/ApplicationDocumentationFileList';
import type { DocumentationPersistHandler } from '../documents-reports/application-documentation/applicationDocumentation.types';
import {
  useStagedApiDocumentationFiles,
  type ApiDocumentationSource,
} from './useStagedApiDocumentationFiles';

export type ApplicationTypeApiDocumentationSectionProps = {
  applicationId: string;
  documentation: ApiDocumentationSource;
  isReadOnly?: boolean;
  onPersistReady: (handler: DocumentationPersistHandler | null) => void;
  onStagedApiFilesChange?: (files: File[]) => void;
};

export function ApplicationTypeApiDocumentationSection({
  applicationId,
  documentation,
  isReadOnly,
  onPersistReady,
  onStagedApiFilesChange,
}: ApplicationTypeApiDocumentationSectionProps) {
  const tCommon = useTranslations('designSystem.common');
  const tDocumentation = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const {
    accept,
    visibleFiles,
    existingFileCount,
    isLoadingDocumentation,
    fileUploadKey,
    supportedFileTypesHint,
    handleFilesAccept,
    removeFile,
  } = useStagedApiDocumentationFiles({
    applicationId,
    documentation,
    onPersistReady,
    onStagedFilesChange: onStagedApiFilesChange,
  });

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      <ApiDocumentationRequiredAlert />
      <FieldWrapper
        label={tDocumentation('categories.apiDocumentation')}
        isRequired
        helperText={supportedFileTypesHint}
      >
        <ApplicationDocumentationCompactFileInput
          accept={accept}
          fileUploadKey={fileUploadKey}
          existingFileCount={existingFileCount}
          onFileAccept={handleFilesAccept}
          disabled={isReadOnly || isLoadingDocumentation}
        />
      </FieldWrapper>
      <ApplicationDocumentationFileList
        files={visibleFiles}
        onRemoveFile={removeFile}
        deleteAriaLabel={tCommon('delete')}
        isReadOnly={isReadOnly}
      />
    </VStack>
  );
}
